/**
 * How a fee is described, shared by the Plans catalog, the enrol wizard and the
 * Enrolments table so they phrase it identically.
 *
 * Two shapes: one payment, or so much a month on the 1st for a season
 * (lib/collections-monthly.ts). The five frequencies and instalment splits
 * this file used to switch across went on 2026-08-25.
 *
 * Rows written before that still carry a `frequency`, so it is rendered where
 * a human would notice its absence, and ignored everywhere else.
 */
import { Membership, Subscription } from '../api/client';

function pounds(pence: number): string {
  return `£${((Number(pence) || 0) / 100).toFixed(2)}`;
}

export interface ChargeDescription {
  /** Short label for a badge. */
  kind: string;
  /** One line a club admin can read at a glance. */
  summary: string;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 'YYYY-MM' → 'Dec 2026'. */
export function monthName(ym: string, withYear = false): string {
  const [y = 0, m = 0] = ym.split('-').map(Number);
  if (!y || !m) return '';
  const name = MONTHS[m - 1] || '';
  return withYear ? `${name} ${y}` : name;
}

/** Payments from first to last month, both included. */
export function monthsBetween(first: string, last: string): number {
  const [fy = 0, fm = 0] = first.split('-').map(Number);
  const [ly = 0, lm = 0] = last.split('-').map(Number);
  return fy && ly ? (ly * 12 + lm) - (fy * 12 + fm) + 1 : 0;
}

function monthly(amount: number, first: string, last: string): string {
  return `${pounds(amount)} a month, ${monthName(first)} to ${monthName(last)} (${monthsBetween(first, last)})`;
}

/** A plan: what it costs. */
export function describePlan(m: Membership): ChargeDescription {
  if (m.billing === 'monthly') return { kind: 'Monthly', summary: monthly(m.totalPrice, m.firstPaymentMonth, m.lastPaymentMonth) };
  return { kind: 'Fee', summary: pounds(m.totalPrice) };
}

/** An enrolment: what this player's place costs. */
export function describeSubscriptionCharge(s: Subscription): ChargeDescription {
  if (s.billing === 'monthly') return { kind: 'Monthly', summary: monthly(s.amount, s.firstPaymentMonth, s.lastPaymentMonth) };
  // Legacy rows carry a cadence. Say so rather than silently describing a
  // £15/month enrolment as if it were a single £15.
  const legacy = String((s as any).frequency || '');
  if (legacy === 'monthly' || legacy === 'quarterly' || legacy === 'annual') {
    return { kind: 'Legacy', summary: `${pounds(s.amount)} (was ${legacy})` };
  }
  return { kind: 'Fee', summary: pounds(s.amount) };
}
